import { useState } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import IconSvg from 'src/assets/icons/IconSvg';
import { useWallet } from 'src/hooks';
import xverseLogo from 'src/assets/images/wallets/xverse.png';
import unisatLogo from 'src/assets/images/wallets/unisat.png';
import magicEdenLogo from 'src/assets/images/wallets/magic-eden.png';
import leatherLogo from 'src/assets/images/wallets/leather.png';
import phantomLogo from 'src/assets/images/wallets/phantom.png';
import okxLogo from 'src/assets/images/wallets/okx.png';
import wizzLogo from 'src/assets/images/wallets/wizz.svg';
import asignaLogo from 'src/assets/images/wallets/asigna.png';

interface Props {
  isOpen: boolean;
  close: () => void;
}

interface WalletItem {
  id: string;
  name: string;
  recommended: boolean;
  logo: string;
  enabled: boolean;
}

const wallets: WalletItem[] = [
  {
    id: 'xverse',
    name: 'Xverse',
    recommended: true,
    logo: xverseLogo,
    enabled: true,
  },
  {
    id: 'magic-eden',
    name: 'Magic Eden',
    recommended: false,
    logo: magicEdenLogo,
    enabled: true,
  },
  {
    id: 'unisat',
    name: 'UniSat',
    recommended: false,
    logo: unisatLogo,
    enabled: true,
  },
  {
    id: 'leather',
    name: 'Leather',
    recommended: false,
    logo: leatherLogo,
    enabled: true,
  },
  {
    id: 'phantom',
    name: 'Phantom',
    recommended: false,
    logo: phantomLogo,
    enabled: true,
  },
  {
    id: 'okx',
    name: 'OKX',
    recommended: false,
    logo: okxLogo,
    enabled: true,
  },
  {
    id: 'wizz',
    name: 'Wizz Wallet',
    recommended: false,
    logo: wizzLogo,
    enabled: true,
  },
  {
    id: 'asigna',
    name: 'Asigna',
    recommended: false,
    logo: asignaLogo,
    enabled: true,
  },
];

const SelectWalletModal = ({ isOpen, close }: Props) => {
  const {
    isXverseInstalled,
    isUnisatInstalled,
    isMagicEdenInstalled,
    isLeatherInstalled,
    isPhantomInstalled,
    isOkxInstalled,
    isWizzInstalled,
    isAsignaInstalled,
    connectXverse,
    connectUnisat,
    connectMagicEden,
    connectLeather,
    connectPhantom,
    connectOkx,
    connectWizz,
    connectAsigna,
  } = useWallet();

  const [connectingWalletId, setConnectingWalletId] = useState<string | null>(null);

  const handleWalletClick = async (walletId: string) => {
    setConnectingWalletId(walletId);
    let connected = false;
    try {
      if (walletId === 'xverse') {
        connected = await connectXverse();
      } else if (walletId === 'unisat') {
        connected = await connectUnisat();
      } else if (walletId === 'magic-eden') {
        connected = await connectMagicEden();
      } else if (walletId === 'leather') {
        connected = await connectLeather();
      } else if (walletId === 'phantom') {
        connected = await connectPhantom();
      } else if (walletId === 'okx') {
        connected = await connectOkx();
      } else if (walletId === 'wizz') {
        connected = await connectWizz();
      } else if (walletId === 'asigna') {
        connected = await connectAsigna();
      }
      if (connected) {
        close();
      }
    } finally {
      setConnectingWalletId(null);
    }
  };

  const isWalletInstalled = (walletId: string): boolean => {
    if (walletId === 'xverse') return isXverseInstalled;
    if (walletId === 'unisat') return isUnisatInstalled;
    if (walletId === 'magic-eden') return isMagicEdenInstalled;
    if (walletId === 'leather') return isLeatherInstalled;
    if (walletId === 'phantom') return isPhantomInstalled;
    if (walletId === 'okx') return isOkxInstalled;
    if (walletId === 'wizz') return isWizzInstalled;
    if (walletId === 'asigna') return isAsignaInstalled;
    return false;
  };

  const getStatusLabel = (wallet: WalletItem): string => {
    if (wallet.enabled) {
      if (connectingWalletId === wallet.id) return 'Connecting...';
      if (isWalletInstalled(wallet.id)) return 'Detected';
      return 'Not Installed';
    }
    if (wallet.recommended) return 'Recommended';
    return 'Detected';
  };

  const getStatusColor = (wallet: WalletItem): string => {
    if (wallet.enabled) {
      if (connectingWalletId === wallet.id) return 'text-brand-tangerine';
      if (isWalletInstalled(wallet.id)) return 'text-grayscale-300';
      return 'text-grayscale-500';
    }
    return wallet.recommended ? 'text-grayscale-300' : 'text-grayscale-500';
  };

  return (
    <Dialog open={isOpen} as="div" className="relative z-10 focus:outline-none" onClose={close}>
      <div className="overflow-y-auto fixed inset-0 w-screen bg-[#1B1B1F99] backdrop-blur-sm xl:top-[80px] top-[68px] xl:bottom-[61px] bottom-[78px] custom-scrollbar">
        <div className="flex justify-center min-h-full xl:items-center xl:p-4">
          <DialogPanel
            transition
            className="w-full xl:max-w-[586px] xl:rounded-lg bg-grayscale-900 xl:px-10 xl:py-6 p-5 relative h-fit"
          >
            <button className="hidden absolute top-5 right-5 xl:block" onClick={close}>
              <IconSvg icon="closeM" className="text-grayscale-400" />
            </button>

            <div className="flex justify-between items-center">
              <h3 className="text-brand-magnolia text-[22px] xl:text-[28px]">Select a wallet</h3>

              <button className="xl:hidden" onClick={close}>
                <IconSvg icon="closeM" className="text-grayscale-400" />
              </button>
            </div>

            <div className="border-t border-grayscale-700 xl:my-4 my-5"></div>

            <div className="space-y-2 xl:space-y-3">
              {wallets.map((wallet) => (
                <button
                  key={wallet.id}
                  className={`flex gap-2 justify-between items-center w-full text-left ${
                    wallet.enabled
                      ? 'cursor-pointer hover:bg-grayscale-800 rounded-lg p-2 -m-2 transition-colors'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => wallet.enabled && handleWalletClick(wallet.id)}
                  disabled={!wallet.enabled || connectingWalletId !== null}
                >
                  <div className="flex gap-2 items-center xl:gap-3">
                    <img
                      src={wallet.logo}
                      alt={wallet.name}
                      className="xl:w-[40px] xl:h-[40px] w-[32px] h-[32px] rounded-full object-cover"
                    />

                    <div className="xl:text-xl text-lg text-brand-magnolia">{wallet.name}</div>
                  </div>

                  <div className={`xl:text-base text-sm ${getStatusColor(wallet)}`}>
                    {getStatusLabel(wallet)}
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-grayscale-700 xl:my-4 my-5"></div>

            <div className="mb-4 text-sm xl:mb-4 xl:text-sm text-grayscale-400">
              By connecting a wallet, you agree to Omega One&apos;s{' '}
              <span className="underline cursor-pointer">Terms of Service</span> and consent to its{' '}
              <span className="underline cursor-pointer">Privacy Policy</span>
            </div>

            <div className="flex gap-2 items-center px-4 py-2 rounded-lg border border-grayscale-700 bg-grayscale-800 w-fit">
              <IconSvg icon="infoM" className="text-grayscale-400" />

              <div className="text-sm text-grayscale-400">
                Hardware wallets are currently not supported
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default SelectWalletModal;
